import { storeToRefs } from "pinia";
import { Loading } from "quasar";
import { computed, ref, toRefs } from "vue";

import { serverError } from "shared/boot/errors";
import { getSource } from "shared/helpers/media";
import activities from "shared/services/activities";
import features from "shared/services/features";
import { useUserStore } from "shared/stores/user";

import { useOrganisationFeaturesStore } from "../stores/organisationFeatures";

export default function useActivities(props) {
  const { mention } = toRefs(props);

  const { refreshUsedCredits } = useOrganisationFeaturesStore();

  const { isAdminUser, organisation } = storeToRefs(useUserStore());

  const alreadyRecordedActivity = ref(false);

  const source = computed(() => {
    if (!mention.value) return {};

    return getSource(mention.value.type);
  });

  const alwaysWarn = computed(
    () =>
      source.value.viewPopupShown === "always" &&
      features.has(source.value.viewWarningFeatureFlag) &&
      (!features.hasCredits(source.value.featureAllowanceFlag) ||
        features.reachedWarning(source.value))
  );

  async function checkActivity({
    noFeature,
    alreadySeen,
    needsRecording,
  } = {}) {
    if (!features.has(source.value.viewFeatureFlag)) {
      features.featureUnavailable(source.value.featureName);

      if (typeof noFeature === "function") {
        return noFeature();
      }

      return false;
    }

    if (mention.value.license === "lexisnexis") {
      return alreadySeen();
    }

    if (isAdminUser.value) {
      if (typeof alreadySeen === "function") {
        return alreadySeen();
      }

      return true;
    }

    try {
      Loading.show({ delay: 300 });

      alreadyRecordedActivity.value =
        (await activities.getActivitiesCount(mention.value)) > 0;

      if (!alreadyRecordedActivity.value) {
        const refreshCreditsPromises = [
          refreshUsedCredits({
            organisationId: organisation.value.id,
            feature: source.value.featureAllowanceFlag,
          }),
        ];

        if (source.value.viewWarningFeatureFlag) {
          refreshCreditsPromises.push(
            refreshUsedCredits({
              organisationId: organisation.value.id,
              feature: source.value.viewWarningFeatureFlag,
              handleErrors: false,
            }).catch((error) => error)
          );
        }

        await Promise.all(refreshCreditsPromises);
      }
    } catch (error) {
      if (error.response.status === 404) {
        return null;
      }

      serverError(error);
    } finally {
      Loading.hide();
    }

    if (!alreadyRecordedActivity.value && alwaysWarn.value) {
      if (typeof needsRecording === "function") {
        return needsRecording();
      }

      return false;
    }

    if (typeof alreadySeen === "function") {
      return alreadySeen();
    }

    return true;
  }

  async function recordActivity() {
    if (source.value.createActivity) {
      await activities.createActivity(mention.value);
    }
  }

  return {
    source,
    alwaysWarn,

    checkActivity,
    recordActivity,
  };
}
